import {
  DealAccessStatus,
  NotificationType,
  useActiveDealAccountQuery,
  useSendNotificationMutation,
  useAccountQuery,
  DealRoleType,
} from "../graphql/generated";
import useGqlClient from "../hooks/useGqlClient";
import { Card } from "../components/Card";
import { LockClosedIcon } from "@heroicons/react/20/solid";
import { useLocation, useParams } from "react-router-dom";
import { Button } from "../components/tailwind/Button";
import { authSelectors } from "../store/auth/selector";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import CompanyLogo from "../components/CompanyLogo";
import { DealNavigationTabs } from "../components/deal_navigation/tabs";
import { useClerk } from "@clerk/clerk-react";
import { actions } from "../store/auth/slice";

export function DealProtection(props: { children: React.ReactNode }) {
  const { dealId: activeDealId } = useParams<{ dealId: string }>();
  const location = useLocation();

  const client = useGqlClient();
  const activeDealAccountQuery = useActiveDealAccountQuery(client, {
    id: activeDealId ? activeDealId : "",
  });
  const account = useSelector(authSelectors.account);
  const [showRemindAdmin, setShowRemindAdmin] = useState(true);

  const sendNotification = useSendNotificationMutation(client);
  const actor = useSelector(authSelectors.actor);

  if (
    location.pathname.startsWith("/deals") ||
    location.pathname.startsWith("/firm") ||
    location.pathname.startsWith("/settings") ||
    location.pathname.startsWith("/create-deal") ||
    location.pathname.startsWith("/tasks")
  ) {
    return null;
  }

  if (!activeDealId) {
    return <>{props.children}</>;
  }

  if (activeDealAccountQuery.isPending) {
    return null;
  }

  if (
    activeDealAccountQuery.error ||
    !activeDealAccountQuery.data ||
    !account
  ) {
    return null;
  }

  const activeDealAccount = activeDealAccountQuery.data.deal.activeDealAccount;

  if (
    activeDealAccountQuery.data.deal.settings.accessRequireGuestMfa &&
    activeDealAccount.dealRole.type === DealRoleType.Guest &&
    !account.mfaEnabled &&
    !actor
  ) {
    return (
      <EnableMfaGate
        dealLogo={activeDealAccountQuery.data.deal.company.logo}
        dealName={activeDealAccountQuery.data.deal.company.name}
        dealLogoColor={activeDealAccountQuery.data.deal.company.logoColor}
      />
    );
  }

  if (
    (activeDealAccount.dealGroup &&
      (activeDealAccount.dealGroup.dealAccessStatus ===
        DealAccessStatus.PendingManualApproval ||
        activeDealAccount.dealGroup.dealAccessStatus ===
          DealAccessStatus.Blocked ||
        activeDealAccount.dealGroup.dealAccessStatus ===
          DealAccessStatus.PendingNda)) ||
    activeDealAccount.dealAccessStatus === DealAccessStatus.PendingNda ||
    activeDealAccount.dealAccessStatus ===
      DealAccessStatus.PendingManualApproval ||
    activeDealAccount.dealAccessStatus === DealAccessStatus.Blocked
  ) {
    return (
      <div className="flex-1 flex justify-center items-center">
        <Card padding="m">
          <div className="flex items-center">
            <LockClosedIcon className="w-5 h-5 text-gray-700" />
            <p className="font-semibold mt-1 ml-1 text-gray-800">Deal locked</p>
          </div>
          <p className="text-gray-500 text-sm mt-2">
            The deal admin needs to grant you access to the deal
          </p>
          {activeDealAccountQuery.data.deal.activeDealAccount
            .dealAccessStatus === DealAccessStatus.PendingManualApproval && (
            <div className="mt-4 flex justify-center">
              <Button
                onClick={() => {
                  if (!showRemindAdmin) {
                    return;
                  }
                  sendNotification.mutate(
                    {
                      input: {
                        type: NotificationType.InviteAccepted,
                        inviteEmail: account?.email ?? "",
                        dealAccountID:
                          activeDealAccountQuery.data.deal.activeDealAccount
                            ?.id,
                        dealID: activeDealId,
                      },
                    },
                    {
                      onSuccess: () => {
                        setShowRemindAdmin(false);
                      },
                    },
                  );
                }}
                isDisabled={!showRemindAdmin}
                text={
                  showRemindAdmin
                    ? "Send Reminder to administrator"
                    : "Reminder Sent"
                }
                variant="positive"
              />
            </div>
          )}
        </Card>
      </div>
    );
  }

  return <>{props.children}</>;
}

function EnableMfaGate(props: {
  dealName: string;
  dealLogo: string;
  dealLogoColor: string;
}) {
  const { openUserProfile } = useClerk();
  const client = useGqlClient();
  const dispatch = useDispatch();

  // Poll the account query to check for MFA enablement
  const accountQuery = useAccountQuery(
    client,
    {},
    {
      refetchInterval(query) {
        if (query.state.data?.account.mfaEnabled) {
          return false;
        }

        return 2000;
      },
    },
  );

  // Update Redux state when account data changes
  useEffect(() => {
    if (accountQuery.data && accountQuery.data.account) {
      dispatch(actions.refreshAccount({ account: accountQuery.data.account }));
    }
  }, [accountQuery.data, dispatch, accountQuery.isRefetching]);

  return (
    <div>
      <div className="px-6 py-4 flex items-center gap-x-2">
        <CompanyLogo
          logo={props.dealLogo ?? undefined}
          withShadow={false}
          borderSize={"2"}
          size={32}
          name={props.dealName}
          bgColor={props.dealLogoColor ?? "#FFF"}
        />
        <p className="font-bold text-gray-800 truncate">{props.dealName}</p>
      </div>

      <Card margin="s xl">
        <div className="relative rounded-md min-h-[500px]">
          {/* Background UI that shows through the frosted glass */}
          <div className="absolute inset-0">
            <DealNavigationTabs />
            <div className="p-6 space-y-6">
              {/* Mock dashboard content */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="h-3 bg-gray-300 rounded mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded"></div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="h-3 bg-gray-300 rounded mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded"></div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="h-3 bg-gray-300 rounded mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded"></div>
                </div>
              </div>

              {/* Mock file list */}
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded">
                    <div className="w-8 h-8 bg-blue-200 rounded"></div>
                    <div className="flex-1">
                      <div className="h-3 bg-gray-300 rounded mb-1 w-3/4"></div>
                      <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded">
                    <div className="w-8 h-8 bg-green-200 rounded"></div>
                    <div className="flex-1">
                      <div className="h-3 bg-gray-300 rounded mb-1 w-2/3"></div>
                      <div className="h-2 bg-gray-200 rounded w-1/3"></div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded">
                    <div className="w-8 h-8 bg-purple-200 rounded"></div>
                    <div className="flex-1">
                      <div className="h-3 bg-gray-300 rounded mb-1 w-4/5"></div>
                      <div className="h-2 bg-gray-200 rounded w-2/5"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mock activity feed */}
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                <div className="space-y-2">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-gray-300 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-3 bg-gray-200 rounded mb-1"></div>
                      <div className="h-2 bg-gray-100 rounded w-3/4"></div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-gray-300 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-3 bg-gray-200 rounded mb-1"></div>
                      <div className="h-2 bg-gray-100 rounded w-2/3"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Frosted glass overlay with MFA content */}
          <div className="absolute inset-0 backdrop-blur-md bg-white/20 flex items-center justify-center rounded-md">
            <div className=" space-y-6 max-w-md px-6">
              <div className="w-16 h-16  bg-persian-100 rounded-full flex items-center justify-center">
                <LockClosedIcon className="w-8 h-8 text-persian-600" />
              </div>
              <div className="space-y-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  Setup multi-factor authentication to access this deal
                </h2>
                <p className="text-gray-600 text-sm leading-normal">
                  In line with the best security practices, the administrators
                  of this deal require you to enable multi-factor authentication
                  before you can gain access.
                </p>

                <p className="text-gray-500 text-xs mt-3">
                  After you click "Enable MFA", navigate to the "Security"
                  section and then click "Add two-step verification".
                </p>
              </div>
              <Button
                text="Enable MFA"
                variant="positive"
                onClick={() => {
                  openUserProfile();
                }}
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
